<template>
  <div v-if="availableLanguages.length > 1" class="relative">
    <!-- Toggle button for exactly two languages -->
    <ion-button 
      v-if="availableLanguages.length === 2"
      class="text-sm h-9" 
      fill="clear" 
      size="small" 
      @click="toggleBetweenTwoLanguages"
      data-cy="language-switcher"
      style="--padding-start: 8px; --padding-end: 8px;"
    >
      <span class="text-base mr-1">{{ getLanguageFlag(currentLocale) }}</span> {{ currentLocale.toUpperCase() }}
    </ion-button>

    <!-- Popover button for three or more languages -->
    <ion-button 
      v-else
      class="text-sm h-9" 
      fill="clear" 
      size="small" 
      @click="openLanguagePopover"
      data-cy="language-switcher"
      style="--padding-start: 8px; --padding-end: 8px;"
    >
      <span class="text-base mr-1">{{ getLanguageFlag(currentLocale) }}</span> {{ currentLocale.toUpperCase() }}
    </ion-button>

    <!-- Popover for three or more languages -->
    <ion-popover 
      v-if="availableLanguages.length > 2"
      :is-open="isPopoverOpen" 
      @didDismiss="isPopoverOpen = false"
      trigger="language-switcher"
      alignment="center"
      side="bottom"
      arrow="true"
    >
      <ion-content>
        <ion-list lines="none">
          <ion-item 
            v-for="lang in availableLanguages" 
            :key="lang.code" 
            button 
            :detail="false"
            :class="{ 'font-medium': currentLocale === lang.code }"
            :style="currentLocale === lang.code ? '--background: var(--ion-color-light)' : ''"
            @click="switchLanguage(lang.code)"
            data-cy="language-option"
          >
            <div class="flex items-center px-4 py-2">
              <span class="text-base mr-1">{{ getLanguageFlag(lang.code) }}</span> {{ lang.name }}
            </div>
          </ion-item>
        </ion-list>
      </ion-content>
    </ion-popover>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { setLanguage } from '../i18n'
import languageFlags from '../i18n/language-flags.json'
import {
  IonButton,
  IonPopover,
  IonContent,
  IonList,
  IonItem
} from '@ionic/vue'

// Type definition for language flags
type LanguageFlags = { [key: string]: string }

const i18n = useI18n()
const isPopoverOpen = ref(false)

// Define props for the component
interface Language {
  code: string
  name: string
}

interface Props {
  languages?: Language[]
}

const props = withDefaults(defineProps<Props>(), {
  languages: () => [
    { code: 'en', name: 'English' },
    { code: 'et', name: 'Eesti' }
  ]
})

const availableLanguages = computed(() => props.languages)
const currentLocale = computed(() => i18n.locale.value)

// Function to get flag emoji based on language code
function getLanguageFlag(langCode: string): string {
  // Get flag from imported mapping or fallback to globe
  return (languageFlags as LanguageFlags)[langCode.toLowerCase()] || '🌐'
}

function openLanguagePopover() {
  isPopoverOpen.value = true
}

// Toggle between two languages
function toggleBetweenTwoLanguages() {
  if (availableLanguages.value.length !== 2) return
  
  const otherLang = availableLanguages.value.find(lang => lang.code !== currentLocale.value)
  if (otherLang) {
    switchLanguage(otherLang.code)
  }
}

function switchLanguage(lang: string) {
  if (lang !== currentLocale.value) {
    setLanguage(lang)
  }
  isPopoverOpen.value = false
}
</script>

<!-- Global styles (not scoped) to hide the icon -->
<style>
.no-icon::part(icon) {
  display: none !important;
}
</style> 